// import { toastWithHaptics as toast } from '@/utility/toast-haptics';
import { toast } from 'sonner';

type MessageType = 'success' | 'error' | 'warning' | 'info';

const titles: Record<MessageType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Notice',
};

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;

/** Display every usable message in an API response body. Returns the number displayed. */
export const ShowApiMessages = ({
  response,
  duration = 6000,
  defaultType = 'info',
}: {
  response: unknown;
  duration?: number;
  defaultType?: MessageType;
}): number => {
  const messages = asRecord(response)?.messages;
  if (!Array.isArray(messages)) return 0;

  let count = 0;
  for (const entry of messages) {
    const item = asRecord(entry);
    if (!item) continue;
    const message = typeof item.message === 'string' ? item.message.trim() : '';
    const title = typeof item.title === 'string' ? item.title.trim() : '';
    if (!message && !title) continue;
    const type =
      typeof item.type === 'string' && ['success', 'error', 'warning', 'info'].includes(item.type)
        ? (item.type as MessageType)
        : defaultType;
    toast[type](title || titles[type], { description: message || undefined, duration });
    count += 1;
  }
  return count;
};

export const ShowResponseMessage = ({ response, duration = 6000 }: { response: unknown; duration?: number }) => {
  if (!ShowApiMessages({ response, duration, defaultType: 'success' })) {
    toast.success('Success', { description: 'Your request has been processed!', duration });
  }
};

/** Accepts caught errors (including Axios errors) or a raw API error response. */
export const ShowErrorResponseMessage = ({ error, duration = 6000 }: { error: unknown; duration?: number }) => {
  const record = asRecord(error);
  const response = asRecord(record?.response);
  if (!ShowApiMessages({ response: response?.data ?? error, duration, defaultType: 'error' })) {
    toast.error('Unable to complete your request', {
      description: response ? 'Something went wrong. Please try again.' : 'Please check your connection and try again.',
      duration,
    });
  }
};
